import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Edit2, Eye, Phone, ToggleLeft, ToggleRight, UserPlus } from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Table from '../components/Table';
import Modal from '../components/Modal';
import { FF, FS, ErrMsg } from '../components/FormField';
import { FilterBar, SearchInput, FilterSelect, PageHeader } from '../components/FilterBar';
import CompanyDeptFilter from '../components/CompanyDeptFilter';
import SearchableSelect from '../components/SearchableSelect';

const STATUSES = ['active','disconnected','suspended','transferred'];
const today = () => new Date().toISOString().slice(0, 10);

const actionLabel = a => ({ assigned:'Assigned', transferred:'Transferred', returned:'Returned', disconnected:'Disconnected', suspended:'Suspended', reconnected:'Reconnected', reallocated:'Reallocated' }[a] || a);
const actionColor = a => ({ assigned:'#34d399', transferred:'#60a5fa', reallocated:'#a78bfa', returned:'#f59e0b', disconnected:'#ef4444', suspended:'#f59e0b', reconnected:'#10b981' }[a] || '#94a3b8');

const Toggle = ({ value, onChange, label, color = '#10b981' }) => (
  <button type="button" onClick={() => onChange(!value)} className="flex items-center gap-2 text-xs transition-colors">
    {value ? <ToggleRight size={20} style={{color}}/> : <ToggleLeft size={20} style={{color:'#334155'}}/>}
    <span style={{color: value ? color : '#475569'}}>{label}</span>
  </button>
);

function HistoryTimeline({ history, loading }) {
  if (loading) return <div className="py-8 text-center text-xs" style={{color:'#334155'}}>Loading...</div>;
  if (!history.length) return <div className="py-8 text-center text-xs" style={{color:'#1e3a5f'}}>No history yet</div>;
  return (
    <div className="space-y-2">
      {history.map((h, i) => (
        <div key={i} className="flex items-start gap-3 p-3 rounded-xl" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.05)'}}>
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{background:`${actionColor(h.action)}18`,color:actionColor(h.action)}}>
            <Phone size={12}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold" style={{color:actionColor(h.action)}}>{actionLabel(h.action)}</div>
            <div className="text-xs mt-0.5" style={{color:'#475569'}}>
              {h.from_name ? <><span style={{color:'#94a3b8'}}>{h.from_name}</span><span style={{color:'#334155'}}> → </span></> : null}
              {h.to_name ? <span style={{color:'#e2e8f0'}}>{h.to_name}</span> : <span style={{color:'#334155'}}>—</span>}
              {h.package_name ? <span style={{color:'#334155'}}> · {h.package_name}</span> : null}
            </div>
            {h.remarks && <div className="text-xs mt-0.5 truncate" style={{color:'#334155'}}>{h.remarks}</div>}
          </div>
          <div className="text-xs whitespace-nowrap flex-shrink-0" style={{color:'#1e3a5f'}}>
            {new Date(h.transfer_date).toLocaleDateString('en-GB')}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Voice() {
  const { isIT, isAdmin, isDirector, isFinance } = useAuth();
  const showCompanyFilter = isAdmin || isDirector || isFinance;
  const [tab, setTab] = useState('active');

  // Active tab state
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [pages, setPages] = useState(1); const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(''); const [filterStatus, setFilterStatus] = useState(''); const [filterIsp, setFilterIsp] = useState('');
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');
  const [filterCompany, setFilterCompany] = useState(null); const [filterDept, setFilterDept] = useState(null);

  // Inactive tab state
  const [inactiveRows, setInactiveRows] = useState([]); const [inactiveTotal, setInactiveTotal] = useState(0); const [inactivePages, setInactivePages] = useState(1); const [inactivePage, setInactivePage] = useState(1);
  const [inactiveLoading, setInactiveLoading] = useState(false);
  const [inactiveSearch, setInactiveSearch] = useState(''); const [inactiveIsp, setInactiveIsp] = useState('');

  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const [isps, setIsps] = useState([]); const [packages, setPackages] = useState([]); const [employees, setEmployees] = useState([]); const [companies, setCompanies] = useState([]);
  const [history, setHistory] = useState([]); const [histLoading, setHistLoading] = useState(false);

  const loadActive = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search; if (filterStatus) params.status = filterStatus; if (filterIsp) params.isp_id = filterIsp;
      if (dateFrom) params.from = dateFrom; if (dateTo) params.to = dateTo;
      if (filterCompany?.length) params.company_id = Array.isArray(filterCompany) ? filterCompany.join(",") : filterCompany;
      if (filterDept) params.department_id = filterDept;
      const r = await api.get('/connections/voice', { params });
      setRows(r.data.data || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, search, filterStatus, filterIsp, dateFrom, dateTo, filterCompany, filterDept]);

  const loadInactive = useCallback(async () => {
    setInactiveLoading(true);
    try {
      const params = { page: inactivePage, limit: 20 };
      if (inactiveSearch) params.search = inactiveSearch; if (inactiveIsp) params.isp_id = inactiveIsp;
      const r = await api.get('/connections/voice/inactive', { params });
      setInactiveRows(r.data.data || []); setInactiveTotal(r.data.total || 0); setInactivePages(r.data.pages || 1);
    } catch {} finally { setInactiveLoading(false); }
  }, [inactivePage, inactiveSearch, inactiveIsp]);

  useEffect(() => { loadActive(); }, [loadActive]);
  useEffect(() => { loadInactive(); }, [loadInactive]);
  useEffect(() => {
    api.get('/connections/isps').then(r => setIsps(r.data || [])).catch(() => {});
    api.get('/employees', { params: { limit: 500, status: 'active' } }).then(r => setEmployees(r.data.data || [])).catch(() => {});
    api.get('/settings/companies').then(r => setCompanies(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.isp_id) api.get('/connections/packages', { params: { isp_id: form.isp_id, type: 'voice' } }).then(r => setPackages(r.data || [])).catch(() => {});
  }, [form.isp_id]);

  const openHistory = async (r) => {
    setSelected(r); setHistLoading(true); setModal('history');
    const h = await api.get(`/connections/history/voice/${r.id}`).then(x => x.data).catch(() => []);
    setHistory(h); setHistLoading(false);
  };

  const handleSave = async e => {
    e.preventDefault();
    if (!form.package_id) { setError('Package / Plan is required.'); return; }
    if (!form.credit_limit) { setError('Credit Limit is required.'); return; }
    setSaving(true); setError('');
    try {
      selected ? await api.put(`/connections/voice/${selected.id}`, form) : await api.post('/connections/voice', form);
      setModal(null); setSelected(null); loadActive();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const handleReallocate = async e => {
    e.preventDefault();
    if (!form.employee_id) { setError('Select an employee.'); return; }
    setSaving(true); setError('');
    try {
      await api.post(`/connections/voice/${selected.id}/reallocate`, {
        employee_id: form.employee_id,
        allocation_date: form.allocation_date || today(),
        remarks: form.remarks
      });
      setModal(null); setSelected(null); loadActive(); loadInactive();
    } catch (err) { setError(err.response?.data?.error || 'Reallocation failed'); } finally { setSaving(false); }
  };

  const statusColor = s => ({ active:'#10b981', disconnected:'#ef4444', suspended:'#f59e0b', transferred:'#818cf8' }[s] || '#94a3b8');

  const activeColumns = [
    { header: 'Number', render: r => <span className="font-mono font-semibold" style={{color:'#34d399'}}>{r.number}</span> },
    { header: 'Company', render: r => <span className="font-mono text-xs font-semibold" style={{color:'#60a5fa'}}>{r.company_code||'—'}</span> },
    { header: 'Holder', render: r => <div><div style={{color:'#e2e8f0',fontWeight:500}}>{r.holder_name||'—'}</div><div className="text-xs" style={{color:'#334155'}}>{r.department||''}</div></div> },
    { header: 'ISP', render: r => <span style={{color:'#94a3b8'}}>{r.isp_name||'—'}</span> },
    { header: 'Package', render: r => <div><div className="text-xs font-medium" style={{color:'#e2e8f0'}}>{r.package_name||'—'}</div>{r.package_cost&&<div className="text-xs" style={{color:'#334155'}}>LKR {Number(r.package_cost).toLocaleString()}/mo</div>}</div> },
    { header: 'Credit Limit', render: r => r.credit_limit ? <span style={{color:'#fbbf24'}}>LKR {Number(r.credit_limit).toLocaleString()}</span> : <span style={{color:'#334155'}}>—</span> },
    { header: 'IDD / Roam', render: r => (
      <div className="flex gap-2 text-xs">
        {r.idd_enabled && <span style={{color:'#60a5fa'}}>IDD</span>}
        {r.voice_roaming_enabled && <span style={{color:'#818cf8'}}>VR</span>}
        {r.data_roaming_enabled && <span style={{color:'#34d399'}}>DR</span>}
        {!r.idd_enabled && !r.voice_roaming_enabled && !r.data_roaming_enabled && <span style={{color:'#1e3a5f'}}>None</span>}
      </div>
    )},
    { header: 'Status', render: r => <span className="text-xs px-2 py-0.5 rounded-full capitalize" style={{background:`${statusColor(r.status)}18`,color:statusColor(r.status),border:`1px solid ${statusColor(r.status)}30`}}>{r.status}</span> },
    { header: '', render: r => (
      <div className="flex gap-1">
        {isIT && <button onClick={() => { setSelected(r); setForm({...r}); setError(''); setModal('form'); }} className="btn-ghost px-2 py-1 text-xs"><Edit2 size={11}/></button>}
        <button onClick={() => openHistory(r)} className="btn-ghost px-2 py-1 text-xs" title="View history" style={{color:'#818cf8'}}><Eye size={11}/></button>
      </div>
    )}
  ];

  const inactiveColumns = [
    { header: 'Number', render: r => <span className="font-mono font-semibold" style={{color:'#94a3b8'}}>{r.number}</span> },
    { header: 'Company', render: r => <span className="font-mono text-xs" style={{color:'#475569'}}>{r.company_code||'—'}</span> },
    { header: 'Last Holder', render: r => <span style={{color:'#94a3b8'}}>{r.holder_name||'Never assigned'}</span> },
    { header: 'ISP', render: r => <span style={{color:'#475569'}}>{r.isp_name||'—'}</span> },
    { header: 'Plan', render: r => <span className="text-xs" style={{color:'#475569'}}>{r.package_name||'—'}</span> },
    { header: 'Status', render: r => <span className="text-xs px-2 py-0.5 rounded-full capitalize" style={{background:`${statusColor(r.status)}18`,color:statusColor(r.status),border:`1px solid ${statusColor(r.status)}30`}}>{r.status}</span> },
    { header: '', render: r => (
      <div className="flex gap-1">
        {isIT && (
          <button onClick={() => { setSelected(r); setForm({ allocation_date: today() }); setError(''); setModal('reallocate'); }}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg font-medium"
            style={{background:'rgba(99,102,241,0.15)',color:'#818cf8',border:'1px solid rgba(99,102,241,0.3)'}}>
            <UserPlus size={11}/> Reallocate
          </button>
        )}
        <button onClick={() => openHistory(r)} className="btn-ghost px-2 py-1 text-xs" title="View history" style={{color:'#818cf8'}}><Eye size={11}/></button>
      </div>
    )}
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="Voice Numbers" count={tab === 'active' ? total : inactiveTotal}>
        <div className="flex items-center gap-2 flex-wrap">
          {showCompanyFilter && <CompanyDeptFilter companyId={filterCompany} deptId={filterDept} onChange={(c,d) => { setFilterCompany(c); setFilterDept(d); setPage(1); }}/>}
          <button onClick={() => { loadActive(); loadInactive(); }} className="btn-ghost p-2"><RefreshCw size={13}/></button>
          {isIT && <button onClick={() => { setSelected(null); setForm({status:'active',idd_enabled:false,voice_roaming_enabled:false,data_roaming_enabled:false}); setError(''); setModal('form'); }} className="btn-primary"><Plus size={13}/> Add Number</button>}
        </div>
      </PageHeader>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl w-fit" style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.06)'}}>
        {[['active','Active',total],['inactive','Inactive',inactiveTotal]].map(([key,label,count]) => (
          <button key={key} onClick={() => setTab(key)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{background:tab===key?'rgba(59,130,246,0.2)':'transparent',color:tab===key?'#60a5fa':'#475569',border:tab===key?'1px solid rgba(59,130,246,0.3)':'1px solid transparent'}}>
            {label} {count > 0 && <span className="text-xs opacity-70">({count})</span>}
          </button>
        ))}
      </div>

      {tab === 'active' && <>
        <FilterBar hasFilters={!!(search||filterStatus||filterIsp||dateFrom||dateTo)} onClear={() => { setSearch(''); setFilterStatus(''); setFilterIsp(''); setDateFrom(''); setDateTo(''); setPage(1); }}>
          <SearchInput value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search number, holder..."/>
          <FilterSelect value={filterIsp} onChange={v => { setFilterIsp(v); setPage(1); }} placeholder="All ISPs" options={isps.map(i=>({value:i.id,label:i.name}))}/>
          <FilterSelect value={filterStatus} onChange={v => { setFilterStatus(v); setPage(1); }} placeholder="All Statuses" options={STATUSES.map(s=>({value:s,label:s}))}/>
          <DateRangePicker from={dateFrom} to={dateTo} onChange={(f,t) => { setDateFrom(f); setDateTo(t); setPage(1); }}/>
        </FilterBar>
        <Table columns={activeColumns} data={rows} loading={loading} page={page} pages={pages} total={total} onPage={setPage}/>
      </>}

      {tab === 'inactive' && <>
        <div className="rounded-2xl p-4" style={{background:'rgba(239,68,68,0.04)',border:'1px solid rgba(239,68,68,0.15)'}}>
          <div className="text-xs" style={{color:'#94a3b8',lineHeight:1.6}}>
            These voice numbers were deactivated — typically when an employee resigned. Use <strong style={{color:'#818cf8'}}>Reallocate</strong> to assign to a new employee and reactivate.
          </div>
        </div>
        <FilterBar hasFilters={!!(inactiveSearch||inactiveIsp)} onClear={() => { setInactiveSearch(''); setInactiveIsp(''); setInactivePage(1); }}>
          <SearchInput value={inactiveSearch} onChange={v => { setInactiveSearch(v); setInactivePage(1); }} placeholder="Search number, last holder..."/>
          <FilterSelect value={inactiveIsp} onChange={v => { setInactiveIsp(v); setInactivePage(1); }} placeholder="All ISPs" options={isps.map(i=>({value:i.id,label:i.name}))}/>
        </FilterBar>
        <Table columns={inactiveColumns} data={inactiveRows} loading={inactiveLoading} page={inactivePage} pages={inactivePages} total={inactiveTotal} onPage={setInactivePage}/>
      </>}

      {/* Add / Edit modal */}
      {modal === 'form' && (
        <Modal title={selected ? `Edit: ${selected.number}` : 'Add Voice Number'} onClose={() => { setModal(null); setSelected(null); }} size="xl">
          <form onSubmit={handleSave} className="space-y-4">
            <ErrMsg msg={error}/>
            <div className="grid grid-cols-2 gap-3">
              <FF label="Number *" name="number" form={form} setForm={setForm} required placeholder="e.g. 0765051000"/>
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>ISP *</label>
                <SearchableSelect required value={form.isp_id||''}
                  onChange={v => setForm({...form,isp_id:v,package_id:''})}
                  options={isps.map(i => ({ value: i.id, label: i.name }))} placeholder="Select ISP"/>
              </div>
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Plan / Package *</label>
                <SearchableSelect required value={form.package_id||''}
                  onChange={v => setForm({...form,package_id:v})}
                  options={packages.map(p => ({ value: p.id, label: p.name,
                    hint: p.monthly_cost ? `LKR ${Number(p.monthly_cost).toLocaleString()} / month` : undefined }))}
                  placeholder="Select plan"/>
              </div>
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Holder (Current User)</label>
                <SearchableSelect value={form.current_holder_id||''}
                  onChange={v => setForm({...form,current_holder_id:v})}
                  options={employees.map(e => ({ value: e.id, label: e.full_name, hint: e.employee_code || undefined }))} placeholder="Unassigned"/>
              </div>
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Company</label>
                <SearchableSelect value={form.company_id||''}
                  onChange={v => setForm({...form,company_id:v})}
                  options={companies.map(c => ({ value: c.id, label: c.name }))} placeholder="Select company"/>
              </div>
              <FF label="Credit Limit (LKR) *" name="credit_limit" type="number" form={form} setForm={setForm} required placeholder="e.g. 1500"/>
              <FF label="Account Number" name="account_number" form={form} setForm={setForm}/>
              <FF label="Purchase Date" name="purchased_date" type="date" form={form} setForm={setForm}/>
              <FS label="Status" name="status" form={form} setForm={setForm} options={STATUSES.map(s=>({val:s,label:s}))}/>
            </div>
            <div className="rounded-xl p-4 space-y-3" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
              <div className="text-xs font-semibold mb-2" style={{color:'#94a3b8'}}>Service Toggles</div>
              <Toggle value={!!form.idd_enabled} onChange={v => setForm({...form,idd_enabled:v})} label="IDD (International Direct Dial)" color="#60a5fa"/>
              <Toggle value={!!form.voice_roaming_enabled} onChange={v => setForm({...form,voice_roaming_enabled:v})} label="Voice Roaming" color="#818cf8"/>
              <div className="flex items-center gap-4">
                <Toggle value={!!form.data_roaming_enabled} onChange={v => setForm({...form,data_roaming_enabled:v})} label="Data Roaming" color="#34d399"/>
                {form.data_roaming_enabled && (
                  <input value={form.data_roaming_package||''} onChange={e => setForm({...form,data_roaming_package:e.target.value})}
                    placeholder="Roaming package name..." className="input-dark flex-1" style={{fontSize:12}}/>
                )}
              </div>
            </div>
            <FF label="Remarks" name="remarks" form={form} setForm={setForm} rows={2}/>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving?'Saving...':'Save'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Reallocate modal */}
      {modal === 'reallocate' && (
        <Modal title={`Reallocate Voice Number: ${selected?.number}`} onClose={() => { setModal(null); setSelected(null); }}>
          <form onSubmit={handleReallocate} className="space-y-4">
            <ErrMsg msg={error}/>
            <div className="px-4 py-3 rounded-xl text-xs" style={{background:'rgba(99,102,241,0.06)',border:'1px solid rgba(99,102,241,0.2)'}}>
              <div className="font-semibold" style={{color:'#a5b4fc'}}>{selected?.number}</div>
              <div style={{color:'#475569'}}>ISP: {selected?.isp_name||'—'} · Last holder: {selected?.holder_name||'None'} · Status: {selected?.status}</div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Allocate To *</label>
              <SearchableSelect required value={form.employee_id||''}
                onChange={v => setForm({...form,employee_id:v})}
                options={employees.map(e => ({ value: e.id, label: e.full_name, hint: e.employee_code || undefined }))} placeholder="Select employee..."/>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Allocation Date *</label>
              <input type="date" required value={form.allocation_date||today()} onChange={e => setForm({...form,allocation_date:e.target.value})} className="input-dark"/>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Remarks</label>
              <textarea value={form.remarks||''} onChange={e => setForm({...form,remarks:e.target.value})} rows={2} className="input-dark" placeholder="Optional notes..."/>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center" style={{background:'linear-gradient(135deg,#6366f1,#4f46e5)'}}>
                {saving ? 'Reallocating...' : 'Reallocate & Activate'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* History modal */}
      {modal === 'history' && (
        <Modal title={`Allocation History — ${selected?.number}`} onClose={() => { setModal(null); setHistory([]); }} size="lg">
          <HistoryTimeline history={history} loading={histLoading}/>
        </Modal>
      )}
    </div>
  );
}
